import { useCallback, useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { AlertType, Clinic, Doctor } from "../models";
import {
	apiAccessRequest,
	SETTING_QUEUE_ENABLE_TELEHEALTH
} from "../constants";

import {
	useAlertContext,
	useClinicContext,
	useConsultContext,
	useDoctorContext,
	useUserContext
} from "../contexts";
import { HubConnection, HubConnectionBuilder } from "@microsoft/signalr";
import { BuildUrl } from "../helpers";

export const SignalREventHandler = () => {
	const [connection, setConnection] = useState<HubConnection | null>(null);
	const { clinics } = useClinicContext();
	const { setRefreshConsults } = useConsultContext();
	const {
		setRefreshTelehealthDoctors,
		telehealthDoctors,
		setRefreshFaceToFaceDoctors,
		faceToFaceDoctors
	} = useDoctorContext();
	const { account } = useUserContext();
	const { showAlert } = useAlertContext();
	const { instance } = useMsal();
	const consultsDebounceTimeout = useRef(0);
	const telehealthDoctorsDebounceTimeout = useRef(0);
	const faceToFaceDoctorsDebounceTimeout = useRef(0);
	const [telehealthClinics, setTelehealthClinics] = useState<Clinic[]>([]);

	const getToken = useCallback(
		async (reconnect?: boolean) => {
			try {
				const tokenResponse = await instance.acquireTokenSilent({
					account,
					scopes: apiAccessRequest.scopes
				});

				if (reconnect) {
					showAlert({
						message: "Reconnected to SignalR hub.",
						alertType: AlertType.success,
						timeout: true
					});
				}

				return tokenResponse.accessToken;
			} catch (error) {
				console.warn("Silent token failed, falling back to popup", error);

				try {
					const tokenResponse = await instance.acquireTokenPopup({
						scopes: apiAccessRequest.scopes
					});

					return tokenResponse.accessToken;
				} catch (popupError) {
					console.error("Popup token failed", popupError);

					showAlert({
						message: "Authentication required. Please log in again.",
						alertType: AlertType.error
					});

					return "";
				}
			}
		},
		[account, instance, showAlert]
	);

	useEffect(() => {
		const connnectToSignalR = async () => {
			const connection = new HubConnectionBuilder()
				.withUrl(BuildUrl("liveUpdateHub"), {
					withCredentials: true,
					accessTokenFactory: () => getToken()
				})
				.withAutomaticReconnect()
				.build();

			connection
				.start()
				.then(() => {
					console.log("Connected to SignalR hub");
				})
				.catch((err) => {
					console.error("Error connecting to SignalR hub:", err);
					showAlert({
						message:
							"Error connecting to SignalR hub. Attempting to reconnect.",
						alertType: AlertType.error,
						timeout: true
					});
					getToken(true);
				});
			setConnection(connection);
		};
		connnectToSignalR();
	}, [account, getToken, instance, showAlert]);

	useEffect(() => {
		if (clinics) {
			setTelehealthClinics(
				clinics?.filter((clinic) =>
					clinic.clinicSettings?.find(
						(setting) =>
							setting.settingName === SETTING_QUEUE_ENABLE_TELEHEALTH &&
							setting.value === "1"
					)
				) || []
			);
		}
	}, [clinics]);

	const debounceSetRefreshConsults = useCallback(() => {
		if (consultsDebounceTimeout.current) {
			clearTimeout(consultsDebounceTimeout.current);
		}

		consultsDebounceTimeout.current = setTimeout(() => {
			console.info("debounceSetRefreshConsults setRefreshConsults");
			setRefreshConsults(true);
		}, 3000) as unknown as number;
	}, [setRefreshConsults]);

	const debounceSetRefreshTelehealthDoctors = useCallback(() => {
		if (telehealthDoctorsDebounceTimeout.current) {
			clearTimeout(telehealthDoctorsDebounceTimeout.current);
		}

		telehealthDoctorsDebounceTimeout.current = setTimeout(() => {
			console.info(
				"debounceSetRefreshTelehealthDoctors setRefreshTelehealthDoctors"
			);
			setRefreshTelehealthDoctors(true);
		}, 2000) as unknown as number;
	}, [setRefreshTelehealthDoctors]);

	const debounceSetRefreshFaceToFaceDoctors = useCallback(() => {
		if (faceToFaceDoctorsDebounceTimeout.current) {
			clearTimeout(faceToFaceDoctorsDebounceTimeout.current);
		}

		faceToFaceDoctorsDebounceTimeout.current = setTimeout(() => {
			console.info(
				"debounceSetRefreshFaceToFaceDoctors setRefreshFaceToFaceDoctors"
			);
			setRefreshFaceToFaceDoctors(true);
		}, 2000) as unknown as number;
	}, [setRefreshFaceToFaceDoctors]);

	useEffect(() => {
		return () => {
			if (consultsDebounceTimeout.current) {
				clearTimeout(consultsDebounceTimeout.current);
			}
			if (telehealthDoctorsDebounceTimeout.current) {
				clearTimeout(telehealthDoctorsDebounceTimeout.current);
			}
		};
	}, []);

	useEffect(() => {
		const consultsHandler = (message: any) => {
			if (message) {
				console.info("consultsHandler", message);
				if (!message.amsClinicIds || message.amsClinicIds.length === 0) {
					return;
				}

				if (
					!telehealthClinics?.some((clinic: Clinic) =>
						message.amsClinicIds.includes(clinic.amsId)
					)
				) {
					return;
				}
				debounceSetRefreshConsults();
			}
		};

		const doctorUpdateHandler = (message: any) => {
			if (message) {
				console.info("doctorUpdateHandler", message);
				const { DoctorId } = JSON.parse(message) as any;
				if (!DoctorId) {
					return;
				}
				if (
					telehealthDoctors?.some((doctor: Doctor) => doctor.id === DoctorId)
				) {
					debounceSetRefreshTelehealthDoctors();
				}
				if (
					faceToFaceDoctors?.some((doctor: Doctor) => doctor.id === DoctorId)
				) {
					debounceSetRefreshFaceToFaceDoctors();
				}
			}
		};

		connection?.on("TelehealthConsultsUpdate", consultsHandler);
		connection?.on("ConsultsUpdate", consultsHandler);
		connection?.on("DoctorsUpdate", doctorUpdateHandler);
		return () => {
			connection?.off("TelehealthConsultsUpdate", consultsHandler);
			connection?.off("ConsultsUpdate", consultsHandler);
			connection?.off("DoctorUpdate", doctorUpdateHandler);
		};
	}, [
		connection,
		showAlert,
		debounceSetRefreshConsults,
		telehealthClinics,
		setRefreshTelehealthDoctors,
		telehealthDoctors,
		debounceSetRefreshTelehealthDoctors,
		faceToFaceDoctors,
		debounceSetRefreshFaceToFaceDoctors
	]);

	return <></>;
};

export default SignalREventHandler;
